import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom } from 'rxjs';

import { WarrantiesService, SalesService, WarrantyAssociateRequest, WarrantyDisassociateRequest, WarrantyActivateRequest, CompanyResponse, ProductCustomisationsService } from '@src/app/shared/backend/clients';

import { WarrantyActivationFormService } from '@app/features/warranties/activate-warranty-process/steps/warranty-activation-form.service';
import { SaleFormService } from '@app/features/sales/add-sale/steps/sale-form.service';
import { FilterOperator } from '@app/shared/enums/FilterOperator.enum';
import { UserRoles } from '@src/app/shared/enums/UserRoles.enum';
import { ToasterService } from '@src/app/shared/components/toaster/toaster.service';
import { SessionService } from '@src/app/shared/services/session.service';

@Component({
    selector: 'app-activate-warranty',
    templateUrl: './activate-warranty.component.html',
    styleUrl: './activate-warranty.component.scss',
    host: { class: 'app-activate-warranty' },
    standalone: false
})
export class StepActivateWarrantyComponent implements OnInit {

  company: CompanyResponse = {
      inventorySubmissionThresholdDays: 0
    };

  @Output() stepSaved = new EventEmitter<void>();

  currentDate: Date = new Date();
  filterOperator = FilterOperator;
  userRoles = UserRoles;

  warrantyActivationFormGroup: FormGroup = this.fb.group({
    manualActivationDate: ['false'],
    activationDate: [this.currentDate]
  });

  productMetadata: any;
  productCustomisationMetadata: any;
  previewImageUrl: string | null = null;
  serialNumber: any;
  sku: any;
  warrantyCode: any;
  itemId: any;
  retailerId: any;
  saleId: any;
  sellerId: any;
  saleMetadata: any;

  constructor(
    private fb: FormBuilder,
    private toasterService: ToasterService,
    private route: ActivatedRoute,
    private warrantyActivationFormService: WarrantyActivationFormService,
    private saleFormService: SaleFormService,
    private salesService: SalesService,
    private productCustomisationsService: ProductCustomisationsService,
    private warrantiesService: WarrantiesService,
    private session: SessionService,
  ) { }

  async ngOnInit() {
    this.company = await this.session.getCompanyData();
    await this.initFormStep();
    this.initForm();
  }

  private async initFormStep() {
    console.log('Retailer ID:', this.warrantyActivationFormService.getStepData('retailerId'));
    console.log('Item data:', this.warrantyActivationFormService.getStepData('itemData'));

    const retailerId = this.warrantyActivationFormService.getStepData('retailerId');
    if (retailerId) {
      this.retailerId = retailerId;
    }

    const itemData = this.warrantyActivationFormService.getStepData('itemData');

    if (itemData) {
      this.productMetadata = itemData.product ?? null;
      this.productCustomisationMetadata = itemData.productCustomisation ?? null;
      this.serialNumber = itemData.serialNumber ?? null;
      this.sku = itemData?.product?.sku ?? itemData?.sku ?? null;
      this.itemId = itemData.itemId ?? itemData.id;
      this.warrantyCode = itemData.warrantyCode ?? itemData.warranties?.[0]?.code ?? null;

      this.setPreviewImageUrl();

      if (this.itemId) {
        await this.getSaleData(this.itemId);
      }
    }

    const saleIdFromUrl = this.route.snapshot.queryParamMap.get('saleId');

    if (saleIdFromUrl && !itemData) {
      await this.loadSaleAndPopulateSteps();
    }

    const warrantyCode = itemData?.warrantyCode ?? itemData?.warranties?.[0]?.code;
    if (itemData?.warrantyCode && !this.warrantyCode) {
      this.warrantyCode = warrantyCode;
    }

    const warrantyCodeData = this.warrantyActivationFormService.getStepData('warrantyCodeData');

    if (warrantyCodeData?.warrantyCode) {
      this.warrantyCode = warrantyCodeData.warrantyCode;
    }

    const activationData = this.warrantyActivationFormService.getStepData('activationData');
    if (activationData) {
      this.warrantyActivationFormGroup.patchValue(activationData);
    }
  }

  private initForm() {
    this.warrantyActivationFormGroup.get('activationDate')?.disable();

    this.warrantyActivationFormGroup.get('manualActivationDate')?.valueChanges.subscribe(value => {
      if (value === 'true') {
        this.warrantyActivationFormGroup.get('activationDate')?.enable();
      } else {
        this.warrantyActivationFormGroup.get('activationDate')?.disable();
      }
    });
  }

  async loadSaleAndPopulateSteps(): Promise<void> {
    try {
      this.saleId = this.route.snapshot.queryParamMap.get('saleId');

      if (!this.saleId) {
        console.log('SaleId is missing in query params');
        return;
      }

      this.saleMetadata = await firstValueFrom(this.salesService.apiSalesIdGet(this.saleId));
      await this.populateStepDataFromSale(this.saleMetadata);

      if (this.itemId) {
        await this.getSaleData(this.itemId);
      }

    } catch (error) {
      console.error('Failed to populate steps:', error);
    }
  }

  private async populateStepDataFromSale(sale: any): Promise<void> {
    const item = sale?.item;
    const product = item?.product;
    const retailer = sale?.retailer;
    const retailerId = retailer?.retailer?.id ?? retailer?.id ?? sale?.retailerId;
    const warrantyCode = item?.warranties?.[0]?.code ?? this.warrantyCode ?? null;

    if (!item || !product || !retailerId) {
      console.warn('Missing data: item, product or retailer id');
      return;
    }

    this.itemId = item.id;
    this.serialNumber = item.serialNumber;
    this.sku = product.sku;
    this.productMetadata = product;
    this.retailerId = retailerId;
    this.warrantyCode = warrantyCode;

    this.productCustomisationMetadata = item.productCustomisation ?? null;

    if (!this.productCustomisationMetadata && item.productCustomisationId) {
      this.productCustomisationMetadata = await this.getProductCustomisation(item.productCustomisationId);
    }

    this.setPreviewImageUrl();

    this.warrantyActivationFormService.setStepData('retailerId', retailerId);
    this.warrantyActivationFormService.setStepData('activationMethod', { activationMethod: 1 });
    this.warrantyActivationFormService.setStepData('itemData', {
      sku: this.sku,
      serialNumber: this.serialNumber,
      product,
      productCustomisation: this.productCustomisationMetadata,
      itemId: this.itemId,
      warrantyCode: this.warrantyCode
    });

    // console.log('Step data populated:', {
    //   retailerId: this.warrantyActivationFormService.getStepData('retailerId'),
    //   activationMethod: this.warrantyActivationFormService.getStepData('activationMethod'),
    //   itemData: this.warrantyActivationFormService.getStepData('itemData')
    // });
  }

  async getSaleData(itemId: any) {
    const filter = {
      pageNumber: 1,
      pageSize: 10,
      filters: [
        { propertyName: 'itemId', operator: this.filterOperator.EQUALS, values: [itemId.toString()] },
        { propertyName: 'isReturned', operator: this.filterOperator.EQUALS, values: ['false'] }
      ]
    };

    try {
      const saleDetails = await firstValueFrom(this.salesService.apiSalesFilterTablePost(filter));
      this.saleId = saleDetails?.results?.[0]?.id ?? null;
      if(!this.warrantyCode)
      {
        this.warrantyCode = saleDetails?.results?.[0]?.warrantyCode ?? null;
      }
      if (!this.saleId) {
        console.warn(`Item ID ${itemId} not found in sale records.`);
      }
    } catch (error) {
      console.error('Failed fetching sale data:', error);
    }
  }

  private async getProductCustomisation(productCustomisationId: number): Promise<any> {
    return await firstValueFrom(
      this.productCustomisationsService.apiProductCustomisationsIdGet(productCustomisationId)
    );
  }

  private setPreviewImageUrl(): void {

    // productType img -> product img -> productCustomisation img
    this.previewImageUrl = this.productCustomisationMetadata?.imageUrl
      ?? this.productMetadata?.imageUrl
      ?? this.productMetadata?.productType?.imageUrl
      ?? null;
  }

  onSaveStepData() {
    this.warrantyActivationFormService.setStepData('activationData', this.warrantyActivationFormGroup.value);
    this.submitAllStepsData();
  }

  private async submitAllStepsData() {
    try {
      const params = await firstValueFrom(this.route.queryParams);
      this.saleId = params['saleId'];

      const saleData = this.saleFormService.getAllData();
      this.itemId = saleData?.saleData?.itemId ?? this.itemId;

      const activationDate = this.warrantyActivationFormGroup.get('activationDate')?.value;
      const utcDate = activationDate?.toISOString();

      const validateSale = async (): Promise<boolean> => {
        const filters: any[] = [];

        // Add filter by sale ID if available
        if (this.saleId) {
          filters.push({
            propertyName: 'id',
            operator: this.filterOperator.EQUALS,
            values: [this.saleId]
          });
        }

        // Otherwise, fall back to item ID if available
        else if (this.itemId) {
          filters.push({
            propertyName: 'itemId',
            operator: this.filterOperator.EQUALS,
            values: [`${this.itemId}`]
          });
        }

        if (filters.length === 0) {
          return false;
        }

        const payload = {
          pageNumber: 1,
          pageSize: 1,
          filters: filters
        };

        try {
          const response = await firstValueFrom(this.salesService.apiSalesFilterTablePost(payload));
          return (response?.totalCount ?? 0) > 0;
        } catch (error) {
          console.error('Error validating sale:', error);
          return false;
        }
      };

      const associateItemToWarranty = async (): Promise<boolean> => {
        const payload: WarrantyAssociateRequest = {
          code: this.warrantyCode,
          itemId: this.itemId,
          operationDate: utcDate,
          retailerId: this.retailerId
        };
        await firstValueFrom(this.warrantiesService.apiWarrantiesAssociatePost(payload));
        return true;
      };

      // rollback WC association from item
      const disassociateItemFromWarranty = async (): Promise<boolean> => {
        const payload: WarrantyDisassociateRequest = {
          code: this.warrantyCode,
          comment: 'Activation failed, triggered rollback to disassociate warranty code.',
          operationDate: utcDate,
          retailerId: this.retailerId
        };
        await firstValueFrom(this.warrantiesService.apiWarrantiesDisassociatePost(payload));
        return true;
      };
      const activateWarranty = async (): Promise<boolean> => {
        const payload: WarrantyActivateRequest = {
          operationDate: utcDate,
          code: this.warrantyCode,
          retailerId: this.retailerId
        };
        try {
          await firstValueFrom(this.warrantiesService.apiWarrantiesActivatePost(payload));
          return true;
        } catch (error) {
          if(!this.company.isPrepaired) {
            await disassociateItemFromWarranty();
          }
          throw error;
        }
      };

      const saleExists = await validateSale();
      if (!saleExists) {
        this.toasterService.show('error', 'Sale not found', 'There is no sale associated with the selected item.');
        return;
      }

      if(!this.company.isPrepaired) {
        await associateItemToWarranty();
      }
      await activateWarranty();

      this.warrantyActivationFormService.setStepData('activationResult', { activationSuccess: true });
      this.stepSaved.emit();
      this.saleFormService.clearData();
    } catch (error) {
      console.error('Error:', error);
      this.warrantyActivationFormService.setStepData('activationResult', { activationSuccess: false });
      this.stepSaved.emit();
    }
  }
}
